import { createSlice } from '@reduxjs/toolkit';
import { WritableDraft } from 'immer/dist/internal';
import Order, { OrderItem, OrderPhase } from 'models/Order';
import ErrorResponse from 'network/responses/ErrorResponse';
import UnassignOrderRequest from '../../network/requests/UnassignOrderRequest';
import OkResponse from '../../network/responses/OkResponse';
import PaginatedResponse from '../../network/responses/PaginatedResponse';
import authenticationAsyncActions from '../actions/authentication.action';
import ordersAsyncActions from '../actions/orders.action';
import postErrorRequest from '../postErrorRequest';
import postRequest from '../postRequest';
import { CPA, OrdersState, PA, Timing } from '../types';

const initialState: OrdersState = {
	hasMorePages: true,
	isPending: false,
	isLoading: false,
	list: [],
	actualList: [],
	scheduledList: [],
	updatedAt: Timing.now(),
	refresh: 0,
	currentWorkOrder: null,
};

const applySortActual = (state: WritableDraft<typeof initialState>) => {
	// Sort actual list

	state.actualList.sort((a, b) => {
		if (a.orderNo < b.orderNo) {
			return -1;
		}
		if (a.orderNo > b.orderNo) {
			return 1;
		}
		return 0;
	});
	state.actualList.sort((a) => {
		if (a.isManualRemoved) {
			return -1;
		}
		return 0;
	});
	state.actualList.sort((a) => {
		if (a.isIncompleted) {
			return -1;
		}
		return 0;
	});
	state.updatedAt = Timing.now();
};

const applySort = (state: WritableDraft<typeof initialState>) => {
	state.list.sort((a, b) => {
		if (a.orderNo < b.orderNo) {
			return -1;
		}
		if (a.orderNo > b.orderNo) {
			return 1;
		}
		return 0;
	});
	state.list.sort((a) => {
		if (a.isManualRemoved) {
			return -1;
		}
		return 0;
	});
	state.list.sort((a) => {
		if (a.isIncompleted) {
			return -1;
		}
		return 0;
	});

	state.updatedAt = Timing.now();
};

const slice = createSlice({
	name: 'orders',
	initialState,
	reducers: {
		setIsLoading: (
			state,
			{
				payload,
			}: {
				payload: boolean;
			}
		) => {
			state.isLoading = payload;
		},
		refresh: (state: WritableDraft<typeof initialState>) => {
			state.refresh = Timing.now();
		},
		updatePhase: (
			state: WritableDraft<typeof initialState>,
			action: PA<{ orderId: string; phase: OrderPhase }>
		) => {
			const findIndex = state.list.findIndex((element) => element.id === action.payload.orderId);
			if (findIndex !== -1) {
				state.list.splice(findIndex, 1, {
					...state.list[findIndex],
					phase: action.payload.phase,
				});
				state.updatedAt = Timing.now();
			}

			const findActualIndex = state.actualList.findIndex((element) => element.id === action.payload.orderId);
			if (findActualIndex !== -1) {
				state.actualList.splice(findActualIndex, 1, {
					...state.actualList[findActualIndex],
					phase: action.payload.phase,
				});
				state.updatedAt = Timing.now();
			}
		},
		updateOrderItem: (
			state: WritableDraft<typeof initialState>,
			action: PA<{ orderId: string; item: OrderItem }>
		) => {
			console.log("inside updateOrderItem action.payload: ",action.payload)
			const findIndex = state.list.findIndex((element) => element.id === action.payload.orderId);
			console.log("findIndex : ",findIndex)
			if (findIndex !== -1) {
				let order = state.list[findIndex]
				const orderItemIndex = order.items.findIndex(i => i.id === action.payload.item.id)
				console.log("orderItemIndex : ",orderItemIndex)
				if (orderItemIndex !== -1) {
					order.items.splice(orderItemIndex, 1, action.payload.item)
					console.log("new order : ",order)
					state.list.splice(findIndex, 1, order);
					state.updatedAt = Timing.now();
				}
			}

			const findActualIndex = state.actualList.findIndex((element) => element.id === action.payload.orderId);
			if (findActualIndex !== -1) {
				let order = state.actualList[findIndex]
				const orderItemIndex = order.items.findIndex(i => i.id === action.payload.item.id)
				if (orderItemIndex !== -1) {
					order.items.splice(orderItemIndex, 1, action.payload.item)
					state.actualList.splice(findIndex, 1, order);
					state.updatedAt = Timing.now();
				}
			}
		},
		scheduleByIds: (state: WritableDraft<typeof initialState>, { payload }: PA<string[]>) => {
			for (const id of payload) {
				const findActualIndex = state.actualList.findIndex((element) => element.id === id);
				if (findActualIndex !== -1) {
					state.scheduledList.push(state.actualList[findActualIndex]);
					state.actualList.splice(findActualIndex, 1);
				}
			}
			applySortActual(state);
		},
		unScheduleByIds: (state: WritableDraft<typeof initialState>, { payload }: PA<string[]>) => {
			for (const id of payload) {
				const findActualIndex = state.scheduledList.findIndex((element) => element.id === id);

				if (findActualIndex !== -1) {
					state.actualList.push(state.scheduledList[findActualIndex]);
					state.scheduledList.splice(findActualIndex, 1);
				}
			}
			applySortActual(state);
		},
		unassignOrder: (
			state: WritableDraft<typeof initialState>,
			action: PA<Pick<UnassignOrderRequest, 'orderId'>>
		) => {
			const findIndex = state.list.findIndex((element) => element.id === action.payload.orderId);
			if (findIndex !== -1) {
				state.list.splice(findIndex, 1, {
					...state.list[findIndex],
					isManualRemoved: true,
				});
				applySort(state);
			}
		},
		sort: (state: WritableDraft<typeof initialState>) => {
			applySort(state);
		},
		setCurrentWorkOrder: (state: WritableDraft<typeof initialState>, action: PA<Order | null>) => {
			state.currentWorkOrder = action.payload;
		},
		updateOrder: (
			state: WritableDraft<typeof initialState>,
			action: PA<{order: Order }>
		) => {
			console.log("Updated Order Socket recieved:")
			const { order } = action.payload;

			const index = state.list.findIndex((element) => element.id === order.id);
			if (index !== -1) {
				console.log("inside first index");
				state.list[index] = order;
			}

			const actualIndex = state.actualList.findIndex((item) => item.id === order.id);
			if (actualIndex !== -1) {
				console.log("inside second index");
				state.actualList[actualIndex] = order;
			}
			state.updatedAt = Timing.now();
		},
		clear: () => initialState,
	},

	extraReducers: {
		[ordersAsyncActions.index.pending.type]: (state) => {
			state.isPending = true;
		},
		[ordersAsyncActions.index.fulfilled.type]: (state, action: CPA<PaginatedResponse<Order>>) => {
			state.hasMorePages = action.payload.hasMorePages;
			state.isPending = false;
			for (const order of action.payload.data) {
				const findIndex = state.list.findIndex((element) => element.id === order.id);
				if (findIndex === -1) {
					state.list.push(order);
				} else {
					state.list.splice(findIndex, 1, order);
				}
			}
			applySort(state);

			postRequest(action);
		},
		[ordersAsyncActions.indexScheduled.fulfilled.type]: (state, action: CPA<Order[]>) => {
			for (const order of action.payload) {
				const findIndex = state.list.findIndex((element) => element.id === order.id);
				if (findIndex === -1) {
					state.list.push(order);
				} else {
					state.list.splice(findIndex, 1, order);
				}
			}
			applySort(state);

			postRequest(action);
		},
		[ordersAsyncActions.indexByIds.fulfilled.type]: (state, action: CPA<Order[]>) => {
			state.isLoading = false;
			state.isPending = false;

			for (const order of action.payload) {
				const findIndex = state.list.findIndex((element) => element.id === order.id);
				if (findIndex === -1) {
					state.list.push(order);
				} else {
					state.list.splice(findIndex, 1, order);
				}
			}
			state.actualList = action.payload;

			applySort(state);
			applySortActual(state);

			if (state.actualList.length > 0) {
				state.currentWorkOrder = state.actualList[0];
			}

			postRequest(action);
		},
		[ordersAsyncActions.addItem.fulfilled.type]: (state, action: CPA<Order>) => {
			const findIndex = state.list.findIndex((element) => element.id === action.payload.id);
			if (findIndex !== -1) {
				state.list.splice(findIndex, 1, action.payload);
			} else {
				state.list.push(action.payload);
			}

			const findActualIndex = state.actualList.findIndex((element) => element.id === action.payload.id);
			if (findActualIndex !== -1) {
				state.actualList.splice(findActualIndex, 1, action.payload);
			} else {
				state.actualList.push(action.payload);
			}
			state.updatedAt = Timing.now();

			postRequest(action);
		},
		[ordersAsyncActions.deleteItem.fulfilled.type]: (state, action: CPA<Order>) => {
			const findIndex = state.list.findIndex((element) => element.id === action.payload.id);
			if (findIndex !== -1) {
				state.list.splice(findIndex, 1, action.payload);
			} else {
				state.list.push(action.payload);
			}

			const findActualIndex = state.actualList.findIndex((element) => element.id === action.payload.id);
			if (findActualIndex !== -1) {
				state.actualList.splice(findActualIndex, 1, action.payload);
			} else {
				state.actualList.push(action.payload);
			}
			state.updatedAt = Timing.now();

			postRequest(action);
		},
		[ordersAsyncActions.update.fulfilled.type]: (state, action: CPA<Order>) => {
			const findIndex = state.list.findIndex((element) => element.id === action.payload.id);
			if (findIndex !== -1) {
				state.list.splice(findIndex, 1, action.payload);
			} else {
				state.list.push(action.payload);
			}

			const findActualIndex = state.actualList.findIndex((element) => element.id === action.payload.id);
			if (findActualIndex !== -1) {
				state.actualList.splice(findActualIndex, 1, action.payload);
			} else {
				state.actualList.push(action.payload);
			}
			state.updatedAt = Timing.now();

			postRequest(action);
		},
		[ordersAsyncActions.sync.fulfilled.type]: (state, action: CPA<Order[]>) => {
			state.list = action.payload;
			state.updatedAt = Timing.now();

			postRequest(action);
		},
		[ordersAsyncActions.override.fulfilled.type]: (state, action: CPA<OkResponse>) => {
			postRequest(action);
		},
		[ordersAsyncActions.index.rejected.type]: (state, action: CPA<ErrorResponse>) => {
			state.isPending = false;

			return postErrorRequest(state, action, initialState);
		},
		[ordersAsyncActions.indexScheduled.rejected.type]: (state, action: CPA<ErrorResponse>) => {
			return postErrorRequest(state, action, initialState);
		},
		[ordersAsyncActions.indexByIds.rejected.type]: (state, action: CPA<ErrorResponse>) => {
			state.isLoading = false;
			state.isPending = false;

			return postErrorRequest(state, action, initialState);
		},
		[ordersAsyncActions.addItem.rejected.type]: (state, action: CPA<ErrorResponse>) =>
			postErrorRequest(state, action, initialState),
		[ordersAsyncActions.deleteItem.rejected.type]: (state, action: CPA<ErrorResponse>) =>
			postErrorRequest(state, action, initialState),
		[ordersAsyncActions.update.rejected.type]: (state, action: CPA<ErrorResponse>) =>
			postErrorRequest(state, action, initialState),
		[ordersAsyncActions.sync.rejected.type]: (state, action: CPA<ErrorResponse>) =>
			postErrorRequest(state, action, initialState),
		[ordersAsyncActions.override.rejected.type]: (state, action: CPA<ErrorResponse>) =>
			postErrorRequest(state, action, initialState),
		[authenticationAsyncActions.signIn.fulfilled.type]: () => initialState,
		[authenticationAsyncActions.signOut.fulfilled.type]: () => initialState,
	},
});

export const ordersActions = slice.actions;

export default slice.reducer;
